import React, { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../../sectepe-ui/index';
import leadSyncService, { Lead, CreateLeadInput, UpdateLeadInput } from '../../services/leadSyncService';
import IcebreakerQuality from '../../components/IcebreakerQuality';
import { fehlerText } from '../format';
import { LEAD_QUELLE, tagsAus } from './leadStatus';

interface Props {
  open: boolean;
  /** Ohne Lead: anlegen. Mit Lead: bearbeiten. */
  lead?: Lead | null;
  onClose: () => void;
  onSaved: (lead: Lead) => void;
}

type Felder = {
  email: string; firstName: string; lastName: string; company: string; jobTitle: string;
  linkedInUrl: string; phone: string; website: string; location: string; source: string; tags: string; notes: string;
  icebreakerEmail: string; icebreakerSubject: string; icebreakerLinkedin: string;
};

const LEER: Felder = {
  email: '', firstName: '', lastName: '', company: '', jobTitle: '', linkedInUrl: '', phone: '', website: '', location: '', source: 'manual', tags: '', notes: '',
  icebreakerEmail: '', icebreakerSubject: '', icebreakerLinkedin: '',
};

const ausLead = (lead: Lead): Felder => ({
  email: lead.email,
  firstName: lead.firstName || '',
  lastName: lead.lastName || '',
  company: lead.company || '',
  jobTitle: lead.jobTitle || '',
  linkedInUrl: lead.linkedInUrl || '',
  phone: lead.phone || '',
  website: lead.website || '',
  location: lead.location || '',
  source: lead.source || 'manual',
  tags: tagsAus(lead).join(', '),
  notes: lead.notes || '',
  icebreakerEmail: lead.icebreakerEmail || '',
  icebreakerSubject: lead.icebreakerSubject || '',
  icebreakerLinkedin: lead.icebreakerLinkedin || '',
});

interface IcebreakerVariant {
  label: string;
  angle?: string;
  subject?: string;
  icebreaker: string;
}
interface IcebreakerVariants {
  email: IcebreakerVariant[];
  linkedin: IcebreakerVariant[];
}

const parseIcebreakerVariants = (raw?: string): IcebreakerVariants => {
  if (!raw) return { email: [], linkedin: [] };
  try {
    const v = JSON.parse(raw);
    return {
      email: Array.isArray(v?.email) ? v.email : [],
      linkedin: Array.isArray(v?.linkedin) ? v.linkedin : [],
    };
  } catch {
    return { email: [], linkedin: [] };
  }
};

const gueltigeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

/** Ein Formular fuer Anlegen und Bearbeiten, damit beide Wege gleich aussehen. */
export const LeadFormular: React.FC<Props> = ({ open, lead, onClose, onSaved }) => {
  const { push } = useToast();
  const [felder, setFelder] = useState<Felder>(LEER);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [variants, setVariants] = useState<IcebreakerVariants>({ email: [], linkedin: [] });
  const [variantenAnzahl, setVariantenAnzahl] = useState(2);
  const [generiert, setGeneriert] = useState(false);

  useEffect(() => {
    if (open) {
      setFelder(lead ? ausLead(lead) : LEER);
      setVariants(parseIcebreakerVariants(lead?.icebreakerVariants));
      setFehler(null);
    }
  }, [open, lead]);

  // Icebreaker fuer genau diesen Lead neu erzeugen; das Formular uebernimmt das Ergebnis.
  const icebreakerNeu = async () => {
    if (!lead) return;
    setGeneriert(true);
    setFehler(null);
    try {
      await leadSyncService.generateIcebreakers([lead.id], {
        channels: ['email', 'linkedin'],
        autoSources: ['website'],
        language: 'de',
        variantCount: variantenAnzahl,
      });
      const frisch = await leadSyncService.getLead(lead.id);
      setVariants(parseIcebreakerVariants(frisch.icebreakerVariants));
      setFelder((alt) => ({
        ...alt,
        icebreakerEmail: frisch.icebreakerEmail || '',
        icebreakerSubject: frisch.icebreakerSubject || '',
        icebreakerLinkedin: frisch.icebreakerLinkedin || '',
      }));
    } catch (err) {
      setFehler(fehlerText(err, 'Fehler beim Generieren des Icebreakers'));
    } finally {
      setGeneriert(false);
    }
  };

  // Variantenauswahl als Knoepfe; die aktuell im Formular stehende Variante ist hervorgehoben.
  const variantenWahl = (liste: IcebreakerVariant[], aktiv: string, waehlen: (v: IcebreakerVariant) => void) => {
    if (liste.length < 2) return null;
    return (
      <div className="btn-group" style={{ flexWrap: 'wrap' }}>
        {liste.map((v, i) => (
          <button
            key={i}
            type="button"
            className={`btn btn-sm ${aktiv === v.icebreaker ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => waehlen(v)}
            title={v.angle || ''}
          >
            {v.label || String.fromCharCode(65 + i)}
          </button>
        ))}
      </div>
    );
  };

  const feld = (name: keyof Felder) => ({
    value: felder[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setFelder((alt) => ({ ...alt, [name]: e.target.value })),
  });

  const speichern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gueltigeEmail(felder.email)) {
      setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
      return;
    }
    const leer = (s: string) => (s.trim() ? s.trim() : undefined);
    const eingabe: CreateLeadInput & UpdateLeadInput = {
      email: felder.email.trim().toLowerCase(),
      firstName: leer(felder.firstName),
      lastName: leer(felder.lastName),
      company: leer(felder.company),
      jobTitle: leer(felder.jobTitle),
      linkedInUrl: leer(felder.linkedInUrl),
      phone: leer(felder.phone),
      website: leer(felder.website),
      location: leer(felder.location),
      source: felder.source || 'manual',
      tags: felder.tags.split(',').map((t) => t.trim()).filter(Boolean),
      notes: leer(felder.notes),
    };
    if (lead) {
      // Icebreaker nur beim Bearbeiten: vor dem Lemlist-Abgleich pruefen und anpassen.
      eingabe.icebreakerEmail = felder.icebreakerEmail;
      eingabe.icebreakerSubject = felder.icebreakerSubject;
      eingabe.icebreakerLinkedin = felder.icebreakerLinkedin;
    }
    setSpeichert(true);
    setFehler(null);
    try {
      const gespeichert = lead
        ? await leadSyncService.updateLead(lead.id, eingabe)
        : await leadSyncService.createLead(eingabe);
      push({ message: lead ? 'Lead gespeichert.' : `Lead ${eingabe.email} angelegt.`, severity: 'success' });
      onSaved(gespeichert);
    } catch (err) {
      setFehler(fehlerText(err, lead ? 'Speichern fehlgeschlagen' : 'Anlegen fehlgeschlagen'));
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <ModalContent className="detail-modal lead-formular">
        <form onSubmit={speichern} aria-label={lead ? 'Lead bearbeiten' : 'Neuer Lead'}>
          <ModalHeader>
            <ModalTitle>{lead ? 'Lead bearbeiten' : 'Neuer Lead'}</ModalTitle>
            <ModalDescription>
              {lead ? 'Änderungen gelten nur in Intermal. Lemlist bekommt sie beim nächsten Abgleich.' : 'Nur die E-Mail-Adresse ist Pflicht – der Rest lässt sich per Clay anreichern.'}
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {fehler && <div className="alert alert-error">{fehler}</div>}
            <div className="akte-felder">
              <label>E-Mail *<input type="email" required autoFocus placeholder="max@beispiel.de" {...feld('email')} /></label>
              <label>
                Quelle
                <select {...feld('source')}>
                  {Object.entries(LEAD_QUELLE).map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
                </select>
              </label>
              <label>Vorname<input type="text" placeholder="Max" {...feld('firstName')} /></label>
              <label>Nachname<input type="text" placeholder="Mustermann" {...feld('lastName')} /></label>
              <label>Firma<input type="text" placeholder="Beispiel GmbH" {...feld('company')} /></label>
              <label>Position<input type="text" placeholder="CISO" {...feld('jobTitle')} /></label>
              <label>LinkedIn<input type="url" placeholder="https://linkedin.com/in/…" {...feld('linkedInUrl')} /></label>
              <label>Telefon<input type="tel" placeholder="+49 …" {...feld('phone')} /></label>
              <label>Website<input type="url" placeholder="https://beispiel.de" {...feld('website')} /></label>
              <label>Ort<input type="text" placeholder="Stuttgart" {...feld('location')} /></label>
              <label>Tags (Komma-getrennt)<input type="text" placeholder="nis2, mittelstand" {...feld('tags')} /></label>
            </div>
            <label className="lead-formular__notizen">
              Notizen
              <textarea className="form-textarea" rows={3} placeholder="Was man vor dem ersten Kontakt wissen sollte" {...feld('notes')} />
            </label>
            {lead && (
              <fieldset className="lead-formular__icebreaker">
                <legend>Icebreaker</legend>
                <p className="text-xs text-secondary">
                  Vor dem Lemlist-Abgleich prüfen und bei Bedarf anpassen. Wird als <code>{'{{icebreaker}}'}</code>,{' '}
                  <code>{'{{icebreakerSubject}}'}</code> und <code>{'{{icebreakerLinkedin}}'}</code> übergeben.
                </p>
                <div className="btn-group" style={{ alignItems: 'center' }}>
                  <label className="text-xs">
                    Varianten{' '}
                    <select value={variantenAnzahl} onChange={(e) => setVariantenAnzahl(Number(e.target.value))} disabled={generiert}>
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </label>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={icebreakerNeu} disabled={generiert}>
                    {generiert ? 'Generiere…' : lead.icebreakerGeneratedAt ? 'Neu generieren' : 'Generieren'}
                  </button>
                </div>
                <label>Betreff (E-Mail)<input type="text" {...feld('icebreakerSubject')} /></label>
                {variantenWahl(variants.email, felder.icebreakerEmail, (v) => setFelder((alt) => ({ ...alt, icebreakerEmail: v.icebreaker, icebreakerSubject: v.subject || '' })))}
                <label>
                  Icebreaker E-Mail
                  <textarea className="form-textarea" rows={3} {...feld('icebreakerEmail')} />
                </label>
                <IcebreakerQuality text={felder.icebreakerEmail} kind="email" contact={{ firstName: felder.firstName, company: felder.company }} />
                {variantenWahl(variants.linkedin, felder.icebreakerLinkedin, (v) => setFelder((alt) => ({ ...alt, icebreakerLinkedin: v.icebreaker })))}
                <label>
                  Icebreaker LinkedIn
                  <textarea className="form-textarea" rows={3} {...feld('icebreakerLinkedin')} />
                </label>
                <IcebreakerQuality text={felder.icebreakerLinkedin} kind="linkedin" contact={{ firstName: felder.firstName, company: felder.company }} />
              </fieldset>
            )}
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={speichert || !felder.email.trim()}>
              {speichert ? 'Speichert…' : lead ? 'Speichern' : 'Anlegen'}
            </button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
